import { useCallback, useEffect, useState } from 'react';
import { Plus, ExternalLink } from 'lucide-react';
import Modal from '../ui/Modal';
import { api } from '../../lib/api';
import {
  StatusBadge, STATUS_LABEL, OWED_LABEL, KIND_LABEL, APPROVER_STATUSES, Field, TextInput, TextArea, Select, MoneyInput,
  BooksAccountSelect, History, Money, Empty, eur, usd, fmtDate, today, btnCls, btn2Cls, thCls, tdCls,
} from './shared';

// The intercompany ledger: every line both ways, with the source document, invoice numbers,
// CCS's rationale and Concria's response. Partners add and edit; Chad marks agreed,
// settled or written off (the server enforces this; the UI only hides what it would refuse).

const CHIP = 'inline-block rounded-full px-2 py-0.5 text-[11px] font-medium';
const CHIP_TONE = {
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  quiet: 'bg-gray-100 text-[var(--text-secondary)] dark:bg-slate-800',
  warn: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
};

/**
 * Where a row stands in Books (LP-309 posting layer). `posting` is the summary's posting state
 * ({ on, cutover_date }); `date` the row's own date; `waitingFor` what it still needs to post
 * (e.g. "Posts when agreed"). Also used by the Payments and Bills tabs.
 */
export function BooksChip({ posting, entryId, date, waitingFor }) {
  if (entryId) return <span className={`${CHIP} ${CHIP_TONE.ok}`} title={`Books entry ${entryId}`}>In Books</span>;
  if (!posting || posting.on == null) return null;
  if (waitingFor) return posting.on ? <span className={`${CHIP} ${CHIP_TONE.quiet}`}>{waitingFor}</span> : null;
  if (posting.cutover_date && date && date <= posting.cutover_date) return <span className={`${CHIP} ${CHIP_TONE.quiet}`} title={`On or before the cutover (${posting.cutover_date}): the QBO history holds it`}>Skipped: before cutover</span>;
  if (!posting.on) return <span className={`${CHIP} ${CHIP_TONE.quiet}`}>Waiting: posting off</span>;
  return <span className={`${CHIP} ${CHIP_TONE.warn}`} title="Listed in Books, Posting">Not posted: see Books, Posting</span>;
}

const POSTS_WHEN_AGREED = ['claimed', 'disputed'];
function LineChip({ line, posting }) {
  if (line.memo_only || line.deleted_at || line.status === 'written_off') return null;
  return <BooksChip posting={posting} entryId={line.books_entry_id} date={line.line_date} waitingFor={POSTS_WHEN_AGREED.includes(line.status) ? 'Posts when agreed' : null} />;
}

export default function LedgerTab({ me, accounts, claims, posting, onChanged }) {
  const [lines, setLines] = useState(null);
  const [status, setStatus] = useState('');
  const [owedBy, setOwedBy] = useState('');
  const [open, setOpen] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const q = new URLSearchParams();
      if (status) q.set('status', status);
      if (owedBy) q.set('owed_by', owedBy);
      setLines((await api.get(`/intercompany/lines?${q}`)).lines); setError('');
    } catch (e) { setError(e.message); }
  }, [status, owedBy]);
  useEffect(() => { load(); }, [load]);

  const saved = () => { setOpen(null); load(); onChanged(); };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        <div className="w-44"><Select value={owedBy} onChange={setOwedBy} options={OWED_LABEL} placeholder="Both directions" /></div>
        <div className="w-44"><Select value={status} onChange={setStatus} options={STATUS_LABEL} placeholder="Any status" /></div>
        <div className="flex-1" />
        <button className={btnCls} onClick={() => setOpen({})}><Plus size={14} /> Add line</button>
      </div>
      {error && <p className="text-sm text-rose-600">{error}</p>}
      {!lines ? <p className="text-sm text-[var(--text-secondary)]">Loading...</p>
        : lines.length === 0 ? <Empty>No lines match.</Empty> : (
          <div className="overflow-x-auto rounded-xl border border-[var(--border)] bg-white dark:bg-slate-900">
            <table className="w-full min-w-[820px] text-sm">
              <thead className="bg-gray-50 dark:bg-slate-800"><tr>
                <th className={thCls}>Date</th><th className={thCls}>Item</th><th className={thCls}>Refs</th>
                <th className={`${thCls} text-right`}>Amount</th><th className={thCls}>Status</th><th className={`${thCls} text-right`}>Settled</th>
              </tr></thead>
              <tbody>
                {lines.map((l) => (
                  <tr key={l.id} onClick={() => setOpen(l)} className={`cursor-pointer border-t border-[var(--border)] hover:bg-gray-50 dark:hover:bg-slate-800 ${l.memo_only ? 'opacity-60' : ''}`}>
                    <td className={`${tdCls} whitespace-nowrap`}>{l.line_date ? fmtDate(l.line_date) : ''}</td>
                    <td className={tdCls}>
                      <div className="text-xs text-[var(--text-secondary)]">{OWED_LABEL[l.owed_by]} · {KIND_LABEL[l.kind]}</div>
                      <div className="text-[var(--text-primary)]">{l.description}</div>
                      {l.concria_response && <div className="mt-0.5 text-xs text-amber-700 dark:text-amber-400">Concria: {l.concria_response}</div>}
                    </td>
                    <td className={`${tdCls} text-xs`}>
                      {l.ccs_invoice_no && <div>CCS #{l.ccs_invoice_no}</div>}
                      {l.pinnacle_ref && <div>Ref {l.pinnacle_ref}</div>}
                      {l.concria_invoice_no && <div>Concria {l.concria_invoice_no}</div>}
                    </td>
                    <td className={`${tdCls} text-right whitespace-nowrap`}>
                      {l.usd_cents == null ? eur(l.eur_cents) : <Money cents={(l.owed_by === 'ccs' ? -1 : 1) * l.usd_cents} />}
                      {l.eur_cents && l.usd_cents != null ? <div className="text-xs text-[var(--text-secondary)]">{eur(l.eur_cents)}</div> : null}
                    </td>
                    <td className={tdCls}><StatusBadge status={l.status} memo={l.memo_only} /><div className="mt-1"><LineChip line={l} posting={posting} /></div></td>
                    <td className={`${tdCls} text-right`}>{l.allocated_cents ? usd(l.allocated_cents) : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="px-3 py-2 text-xs text-[var(--text-secondary)]">Positive amounts are owed by Concria to CCS; negative amounts are owed by CCS to Concria.</p>
          </div>
        )}
      {open && <LineModal line={open.id ? open : null} me={me} accounts={accounts} claims={claims} posting={posting} onClose={() => setOpen(null)} onSaved={saved} />}
    </div>
  );
}

const EDIT_FIELDS = ['owed_by', 'kind', 'line_date', 'description', 'amount_cents', 'eur_cents', 'fx_rate', 'source_url', 'pinnacle_ref', 'ccs_invoice_no',
  'concria_invoice_no', 'billed_on', 'addressed_to', 'ccs_rationale', 'concria_response', 'status', 'memo_only', 'overlap_note', 'note', 'claim_id'];

function LineModal({ line, me, accounts, claims, posting, onClose, onSaved }) {
  const [f, setF] = useState(() => (line ? Object.fromEntries(EDIT_FIELDS.map((k) => [k, line[k] ?? (k === 'memo_only' ? false : '')])) : { owed_by: 'concria', kind: 'storage', status: 'claimed', memo_only: false, line_date: today() }));
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [postAcct, setPostAcct] = useState(null);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const locked = line && APPROVER_STATUSES.includes(line.status) && !me.can_approve;

  useEffect(() => { if (line) api.get(`/intercompany/lines/${line.id}`).then(setDetail).catch(() => {}); }, [line]);

  const statusOptions = Object.fromEntries(Object.entries(STATUS_LABEL).filter(([k]) => me.can_approve || !APPROVER_STATUSES.includes(k) || k === line?.status));

  const save = async () => {
    setBusy(true); setError('');
    try {
      const body = {};
      for (const k of EDIT_FIELDS) {
        const v = f[k];
        if (line && (v ?? '') === (line[k] ?? '')) continue;
        if (!line && (v === '' || v == null)) continue;
        body[k] = v === '' ? null : v;
      }
      if (!me.can_approve) delete body.memo_only;
      if (line) await api.patch(`/intercompany/lines/${line.id}`, { ...body, revision: line.updated_at });
      else await api.post('/intercompany/lines', body);
      onSaved();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this line? Chad can restore it.')) return;
    try { await api.delete(`/intercompany/lines/${line.id}`); onSaved(); } catch (e) { setError(e.message); }
  };
  const postToBooks = async (allowBefore = false) => {
    setBusy(true); setError('');
    try {
      await api.post(`/intercompany/lines/${line.id}/post-to-books`, { account_id: postAcct, allow_before_cutover: allowBefore });
      onSaved();
    } catch (e) {
      if (e.status === 409 && /cutover/.test(e.message) && window.confirm(`${e.message}\n\nPost anyway?`)) { setBusy(false); return postToBooks(true); }
      setError(e.message);
    } finally { setBusy(false); }
  };

  return (
    <Modal title={line ? 'Ledger line' : 'Add a ledger line'} onClose={onClose} maxWidth="max-w-3xl">
      {locked && <p className="mb-3 rounded-lg bg-amber-50 p-2 text-xs text-amber-800 dark:bg-amber-900/30 dark:text-amber-300">This line is {STATUS_LABEL[line.status].toLowerCase()}. You can add notes and responses; Chad changes the amount or status.</p>}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Direction"><Select value={f.owed_by} onChange={set('owed_by')} options={OWED_LABEL} /></Field>
        <Field label="Kind"><Select value={f.kind} onChange={set('kind')} options={KIND_LABEL} /></Field>
        <Field label="Description" className="sm:col-span-2"><TextInput value={f.description} onChange={set('description')} /></Field>
        <Field label="Date"><TextInput type="date" value={f.line_date} onChange={set('line_date')} /></Field>
        <Field label="Status"><Select value={f.status} onChange={set('status')} options={statusOptions} /></Field>
        <Field label="Amount (USD)"><MoneyInput cents={f.amount_cents || null} onChange={set('amount_cents')} /></Field>
        <div className="grid grid-cols-2 gap-2">
          <Field label="Amount (EUR, optional)"><MoneyInput cents={f.eur_cents || null} onChange={set('eur_cents')} /></Field>
          <Field label="FX (USD per EUR)"><TextInput value={f.fx_rate} onChange={set('fx_rate')} placeholder="1.08" /></Field>
        </div>
        <Field label="CCS invoice no."><TextInput value={f.ccs_invoice_no} onChange={set('ccs_invoice_no')} /></Field>
        <Field label="Pinnacle / vendor ref"><TextInput value={f.pinnacle_ref} onChange={set('pinnacle_ref')} /></Field>
        <Field label="Concria invoice no."><TextInput value={f.concria_invoice_no} onChange={set('concria_invoice_no')} /></Field>
        <Field label="Re-billed on invoice"><TextInput value={f.billed_on} onChange={set('billed_on')} placeholder="e.g. 1033" /></Field>
        <Field label="Addressed to (Aki, Riikka...)"><TextInput value={f.addressed_to} onChange={set('addressed_to')} /></Field>
        <Field label="Linked claim"><Select value={f.claim_id} onChange={set('claim_id')} options={Object.fromEntries((claims || []).map((c) => [c.id, c.title]))} placeholder="None" /></Field>
        <Field label="Source document link" className="sm:col-span-2"><TextInput value={f.source_url} onChange={set('source_url')} placeholder="https://" /></Field>
        <Field label="CCS rationale" className="sm:col-span-2"><TextArea value={f.ccs_rationale} onChange={set('ccs_rationale')} /></Field>
        <Field label="Concria's response" className="sm:col-span-2"><TextArea value={f.concria_response} onChange={set('concria_response')} /></Field>
        <Field label="Overlap / double-count note" className="sm:col-span-2"><TextArea value={f.overlap_note} onChange={set('overlap_note')} rows={2} /></Field>
        <Field label="Internal note (never on the statement)" className="sm:col-span-2"><TextArea value={f.note} onChange={set('note')} rows={2} /></Field>
        {me.can_approve && (
          <label className="flex items-center gap-2 text-sm sm:col-span-2">
            <input type="checkbox" checked={!!f.memo_only} onChange={(e) => set('memo_only')(e.target.checked)} />
            Reference only (shown, never counted; e.g. a combined re-bill of lines already listed)
          </label>
        )}
      </div>
      {line?.source_url && <a href={line.source_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-primary"><ExternalLink size={12} /> Open source document</a>}
      {error && <p className="mt-3 text-sm text-rose-600">{error}</p>}
      <div className="mt-4 flex flex-wrap gap-2">
        <button className={btnCls} disabled={busy} onClick={save}>{line ? 'Save changes' : 'Add line'}</button>
        {line && (!locked || me.can_approve) && <button className={btn2Cls} onClick={remove}>Delete</button>}
      </div>

      {detail && (
        <div className="mt-5 space-y-2 border-t border-[var(--border)] pt-4 text-sm">
          <h3 className="font-semibold text-[var(--text-primary)]">Settlement</h3>
          {detail.allocations.length === 0 ? <p className="text-xs text-[var(--text-secondary)]">No payment matched yet. Match one from the Payments tab.</p>
            : <ul className="text-xs">{detail.allocations.map((a) => <li key={a.id}>{usd(a.amount_cents)} from payment {a.payment_id.slice(0, 8)} ({fmtDate(a.created_at.slice(0, 10))})</li>)}</ul>}
          <h3 className="pt-2 font-semibold text-[var(--text-primary)]">Books</h3>
          {posting?.on && (
            <p className="text-xs text-[var(--text-secondary)]">
              Posting is on: the line posts to Books by itself when Chad marks it agreed; writing it off reverses that entry.
            </p>
          )}
          {line.books_entry_id ? <p className="text-xs"><LineChip line={line} posting={posting} /> Posted to Books (entry {line.books_entry_id.slice(0, 8)}).</p>
            : posting?.on ? <p className="text-xs"><LineChip line={line} posting={posting} /></p>
            : me.can_post ? (
              <div className="flex flex-wrap items-end gap-2">
                <div className="min-w-[240px] flex-1">
                  <Field label={line.owed_by === 'concria' ? 'Cost being re-billed (credit side; debit is Due from Concria Oy)' : 'What CCS bought (debit side; credit is A/P Concria Oy)'}>
                    <BooksAccountSelect accounts={accounts} value={postAcct} onChange={setPostAcct} types={['cogs', 'expense', 'asset', 'income']} />
                  </Field>
                </div>
                <button className={btn2Cls} disabled={!postAcct || busy} onClick={() => postToBooks(false)}>Post to Books</button>
              </div>
            ) : <p className="text-xs text-[var(--text-secondary)]">Not in Books. Chad posts lines to Books.</p>}
          <History table="lines" id={line.id} />
        </div>
      )}
    </Modal>
  );
}
